import { authModalService } from '@/services/authModal';

/** 后端登录成功后设置的 Cookie 名称 */
export const AUTH_TOKEN_COOKIE_KEY = 'EASESOURCING_TOKEN';

/** visitor_id 的 Cookie 名称（后端或本地开发从 URL 参数 __dev_visitor_id__ 写入） */
export const VISITOR_ID_COOKIE_KEY = 'visitor_id';

/** OAuth 重定向标记（sessionStorage key），用于 Google 等 OAuth 回调后触发 broadcastAuthSuccess */
export const OAUTH_REDIRECT_FLAG_KEY = 'oauth_redirect';

/**
 * 清除 OAuth 回调标记
 * 在 fetchUser 成功 broadcast 后、logout、clearUser 时调用
 */
export function clearOAuthRedirectFlag(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(OAUTH_REDIRECT_FLAG_KEY);
}

/**
 * 清除访客 ID Cookie（logout、clearUser 时调用）
 */
export function clearVisitorId(): void {
  deleteCookieAllDomains(VISITOR_ID_COOKIE_KEY);
}

/**
 * 设置 OAuth 重定向标记（跳转 Google 等 OAuth 前调用）
 */
export function setOAuthRedirectFlag(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(OAUTH_REDIRECT_FLAG_KEY, '1');
}

/** 认证相关错误码 */
export enum AuthErrorCode {
  /** 未登录 */
  NOT_LOGGED_IN = 'NOT_LOGGED_IN',
}

/** 认证错误，携带错误码便于业务判断 */
export class AuthError extends Error {
  code: AuthErrorCode;

  constructor(code: AuthErrorCode) {
    super(code);
    this.name = 'AuthError';
    this.code = code;
  }
}

/**
 * 从 document.cookie 中解析指定 key 的值
 */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${encodeURIComponent(name)}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * 遍历所有可能的父域名组合删除指定 Cookie。
 * 删除 Cookie 时 domain 必须与设置时完全一致，否则无法删除。
 * 后端可能将 Cookie 设置在任意层级的父域（如 .easesourcing.com），
 * 而非当前子域（如 .test.easesourcing.com），因此逐一尝试所有组合。
 */
function deleteCookieAllDomains(name: string): void {
  if (typeof document === 'undefined') return;
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
  // 不带 domain 属性（精确匹配当前 hostname 设置的 cookie）
  document.cookie = `${name}=; ${expired}`;
  const hostname = document.location.hostname;
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
    return;
  }
  // 依次尝试从当前域到顶级父域的所有 .xxx 组合
  const parts = hostname.split('.');
  for (let i = 0; i < parts.length - 1; i++) {
    const domain = '.' + parts.slice(i + 1).join('.');
    document.cookie = `${name}=; ${expired}; domain=${domain}`;
  }
}

/**
 * 清除认证 Cookie（退出登录时调用）
 */
export function clearAuthCookie(): void {
  deleteCookieAllDomains(AUTH_TOKEN_COOKIE_KEY);
}

/**
 * 判断是否处于登录态
 * 通过检查 Cookie 中是否存在 EASESOURCING_TOKEN 来判断
 * @returns true 表示已登录，false 表示未登录
 */
export function isLoggedIn(): boolean {
  const token = getCookie(AUTH_TOKEN_COOKIE_KEY);
  return Boolean(token && token.trim() !== '');
}

/**
 * 确保用户已登录
 * - 已登录：直接返回，继续流程
 * - 未登录：唤起登录弹窗，抛出 AuthError 中断流程（不等待登录结果）
 */
export function ensureLogin(): void {
  if (isLoggedIn()) {
    return;
  }
  authModalService.openLogin();
  throw new AuthError(AuthErrorCode.NOT_LOGGED_IN);
}
